import type { ApiErrorBody } from '@myscaw/shared';

/**
 * FND-7.4 / uml/20-logging-errors-secrets.md sections 3.2 and 3.4.
 *
 * Every failed request becomes one of these. The status decides the behaviour
 * (section 3.4) and the envelope carries what to show.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;
  /** From a 429's `Retry-After` header, when the throttler sent one. RO03. */
  readonly retryAfterSeconds: number | null;

  constructor(status: number, body: ApiErrorBody | null, retryAfterSeconds: number | null = null) {
    // `message` is English and exists for the console and for a stack trace.
    // Nothing renders it. NFR04.
    super(body?.message ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** `RATING_REQUIRES_CHECK_IN`, or null when there was no envelope. */
  get code(): string | null {
    return this.body?.code ?? null;
  }

  /** The i18n key the interface renders. Falls back per status. */
  get messageKey(): string {
    return this.body?.messageKey ?? fallbackKey(this.status);
  }

  /**
   * The envelope's `details`, as i18n interpolation values: `{ day, seats }`
   * fills "Le {{day}} est déjà complet". Anything but a plain object is no
   * values at all, so a sentence never renders an array's indexes.
   */
  get params(): Record<string, unknown> {
    const details = this.body?.details;
    return details != null && typeof details === 'object' && !Array.isArray(details)
      ? (details as Record<string, unknown>)
      : {};
  }

  /** `RG67`, when the server refused because of a rule. */
  get rule(): string | null {
    return this.body?.rule ?? null;
  }

  /**
   * The only thing worth showing a user on a 500. It is the string that finds
   * the stack in the log, and the stack is the only place it lives.
   */
  get requestId(): string | null {
    return this.body?.requestId ?? null;
  }

  get isAuthentication(): boolean {
    return this.status === 401;
  }

  get isPermission(): boolean {
    return this.status === 403;
  }

  /** 422 and 409: the request was understood and refused. */
  get isRefusal(): boolean {
    return this.status === 409 || this.status === 422;
  }

  get isServer(): boolean {
    return this.status >= 500;
  }
}

/**
 * A network failure never reaches the server, so there is no envelope and no
 * status. It is still an ApiError so that one catch handles both.
 */
export class NetworkError extends ApiError {
  // Declared here rather than using Error.cause: that is ES2022 and this app
  // targets ES2020, so the base type does not have it.
  readonly cause?: unknown;

  constructor(cause?: unknown) {
    super(0, null);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

function fallbackKey(status: number): string {
  switch (status) {
    case 0:
      return 'common.errors.network';
    case 401:
      return 'common.errors.unauthenticated';
    case 403:
      return 'common.errors.forbidden';
    case 404:
      return 'common.errors.notFound';
    case 429:
      return 'common.errors.tooManyRequests';
    default:
      return status >= 500 ? 'common.errors.internal' : 'common.errors.requestFailed';
  }
}
